import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Search, X, RotateCcw, SlidersHorizontal } from 'lucide-react';
import styles from '../DocumentCentre.module.css';
import {
  DISPATCH_FILTER_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  formatDate,
} from '../documentCentre.constants';

export const EMPTY_FILTERS = {
  q: '',
  types: [],
  dateType: '',
  dateFrom: '',
  dateTo: '',
  paymentStatus: '',
  dispatchStatus: '',
  bookingStatus: '',
  destination: '',
  supplier: '',
  release: '',
  includeHistorical: false,
};

const DATE_TYPES = [
  { value: 'travel', label: 'Travel date' },
  { value: 'document', label: 'Document date' },
  { value: 'due', label: 'Due date', financialOnly: true },
];

const TypeMultiSelect = ({ types, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const selected = new Set(value);
  const label = !value.length
    ? 'All types'
    : value.length === 1
      ? types.find((t) => t.value === value[0])?.label || '1 type'
      : `${value.length} types`;

  const toggle = (type) => {
    const next = new Set(selected);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    onChange([...next]);
  };

  return (
    <div className={styles.multi} ref={ref}>
      <button type="button" className={styles.multiButton} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{label}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className={styles.multiMenu} role="listbox" aria-multiselectable="true">
          {types.map((type) => (
            <label
              key={type.value}
              className={`${styles.multiOption} ${type.available ? '' : styles.multiOptionDisabled}`}
              title={type.available ? undefined : 'Finance does not issue this document type yet'}
            >
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={selected.has(type.value)}
                disabled={!type.available}
                onChange={() => toggle(type.value)}
              />
              {type.label}
              {!type.available && <span className={styles.multiNote}>none issued yet</span>}
            </label>
          ))}
          {value.length > 0 && (
            <button type="button" className={styles.moreToggle} style={{ padding: '6px 10px' }} onClick={() => onChange([])}>
              Show all types
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/** Labels for the active (applied) filters, for the chips (spec §5: keep active filters visible). */
export const activeFilterChips = (applied, types) => {
  const chips = [];
  if (applied.q) chips.push({ key: 'q', label: `Search: ${applied.q}` });
  if (applied.types.length) {
    chips.push({
      key: 'types',
      label: `Type: ${applied.types.map((t) => types.find((x) => x.value === t)?.label || t).join(', ')}`,
    });
  }
  if (applied.dateType && (applied.dateFrom || applied.dateTo)) {
    const name = DATE_TYPES.find((d) => d.value === applied.dateType)?.label || 'Date';
    chips.push({
      key: 'date',
      label: `${name}: ${applied.dateFrom ? formatDate(applied.dateFrom) : '...'} to ${applied.dateTo ? formatDate(applied.dateTo) : '...'}`,
    });
  }
  if (applied.paymentStatus) chips.push({ key: 'paymentStatus', label: `Payment: ${applied.paymentStatus}` });
  if (applied.dispatchStatus) {
    chips.push({
      key: 'dispatchStatus',
      label: `Dispatch: ${DISPATCH_FILTER_OPTIONS.find((o) => o.value === applied.dispatchStatus)?.label}`,
    });
  }
  if (applied.bookingStatus) chips.push({ key: 'bookingStatus', label: `Booking: ${applied.bookingStatus}` });
  if (applied.destination) chips.push({ key: 'destination', label: `Destination: ${applied.destination}` });
  if (applied.supplier) chips.push({ key: 'supplier', label: `Supplier: ${applied.supplier}` });
  if (applied.release) chips.push({ key: 'release', label: applied.release === 'eligible' ? 'Eligible only' : 'Blocked only' });
  if (applied.includeHistorical) chips.push({ key: 'includeHistorical', label: 'Including historical' });
  return chips;
};

const CentreFilters = ({ section, draft, setDraft, applied, types, options, onApply, onClear, onRemoveChip, loading }) => {
  const [more, setMore] = useState(
    Boolean(applied.bookingStatus || applied.destination || applied.supplier || applied.release || applied.includeHistorical)
  );
  const set = (key) => (event) => {
    const value = event?.target ? (event.target.type === 'checkbox' ? event.target.checked : event.target.value) : event;
    setDraft((prev) => ({ ...prev, [key]: value }));
  };
  const chips = activeFilterChips(applied, types);
  const dateTypes = DATE_TYPES.filter((d) => !d.financialOnly || section === 'invoices' || section === 'all');

  const submit = (event) => {
    event.preventDefault();
    onApply();
  };

  return (
    <form className={styles.filterPanel} onSubmit={submit}>
      <div className={styles.filterRow}>
        <div className={`${styles.field} ${styles.fieldWide}`}>
          <span className={styles.fieldLabel}>Search</span>
          <input
            className={styles.input}
            type="search"
            placeholder="Document number, booking reference or customer"
            value={draft.q}
            onChange={set('q')}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Document type</span>
          <TypeMultiSelect types={types} value={draft.types} onChange={set('types')} />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Date</span>
          <div className={styles.dateRange}>
            <select className={styles.select} value={draft.dateType} onChange={set('dateType')} aria-label="Date type">
              <option value="">Any date</option>
              {dateTypes.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
            <input
              className={styles.input}
              type="date"
              value={draft.dateFrom}
              onChange={set('dateFrom')}
              disabled={!draft.dateType}
              aria-label="From date"
            />
            <input
              className={styles.input}
              type="date"
              value={draft.dateTo}
              onChange={set('dateTo')}
              disabled={!draft.dateType}
              aria-label="To date"
            />
          </div>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Payment status</span>
          <select className={styles.select} value={draft.paymentStatus} onChange={set('paymentStatus')}>
            <option value="">Any payment status</option>
            {PAYMENT_STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Dispatch status</span>
          <select className={styles.select} value={draft.dispatchStatus} onChange={set('dispatchStatus')}>
            {DISPATCH_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {more && (
        <div className={styles.filterRow}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Booking status</span>
            <select className={styles.select} value={draft.bookingStatus} onChange={set('bookingStatus')}>
              <option value="">Any booking status</option>
              {['Confirmed', 'Pending', 'Cancelled', 'Completed', ...(options.bookingStatuses || [])]
                .filter((v, i, all) => all.indexOf(v) === i)
                .map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
            </select>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Destination</span>
            <select className={styles.select} value={draft.destination} onChange={set('destination')}>
              <option value="">Any destination</option>
              {(options.destinations || []).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Supplier</span>
            <select className={styles.select} value={draft.supplier} onChange={set('supplier')}>
              <option value="">Any supplier</option>
              {(options.suppliers || []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Release</span>
            <select className={styles.select} value={draft.release} onChange={set('release')}>
              <option value="">Eligible and blocked</option>
              <option value="eligible">Eligible only</option>
              <option value="blocked">Blocked only</option>
            </select>
          </div>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={draft.includeHistorical}
              onChange={set('includeHistorical')}
            />
            Include historical versions
          </label>
        </div>
      )}

      <div className={styles.filterRow} style={{ alignItems: 'center' }}>
        <button type="button" className={styles.moreToggle} onClick={() => setMore((m) => !m)}>
          <SlidersHorizontal size={14} /> {more ? 'Fewer filters' : 'More filters'}
        </button>
        <div className={styles.filterActions}>
          <button type="button" className={styles.secondaryBtn} onClick={onClear} disabled={loading}>
            <RotateCcw size={14} /> Clear filters
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={loading}>
            <Search size={14} /> Apply filters
          </button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className={styles.chips}>
          <span className={styles.chipsLabel}>Active filters</span>
          {chips.map((chip) => (
            <span key={chip.key} className={styles.chip}>
              {chip.label}
              <button type="button" onClick={() => onRemoveChip(chip.key)} aria-label={`Remove filter ${chip.label}`}>
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}
    </form>
  );
};

export default CentreFilters;
